import React, { FC } from 'react';
import Highlighter from 'react-highlight-words';
import { Link } from 'react-router-dom';

import Utils from '../utils';
import { GenreSidebarOut, PerformerSidebarOut, PublisherSidebarOut } from '../../store/api';
import { Trans } from 'react-i18next';
import { useEnvironment } from '../components/EnvironmentContext';
import { MULTI_LANGUAGE_SHOP, useFeature } from '../utils/togglz';
import MetadataUtils from '../utils/utils-metadata';

type SearchRelatedAuthorProps = {
    readonly author: PerformerSidebarOut;
    readonly keyWord?: string;
    readonly onSearchRelatedClickResetSearch: () => void;
    readonly shopLanguage: string | undefined;
};

export const SearchRelatedAuthor: FC<SearchRelatedAuthorProps> = ({
    author,
    keyWord,
    onSearchRelatedClickResetSearch,
    shopLanguage
}) => {
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const env = useEnvironment();

    const performerPhotoUrl = MetadataUtils.performerPhotoUrl(author.id, env);
    const performerFullname = MetadataUtils.getPerformerFullname(author);

    return (
        <Link
            key={author.id}
            className="person related-element"
            to={Utils.topHitLocationDescriptor({
                filter: { author: { include: [author.id] } },
                scope: null,
                topHitLabel: performerFullname,
                shopLanguage: shopLanguage,
                isMultiLanguageShopEnabled,
                ageCategory: undefined
            })}
            onClick={onSearchRelatedClickResetSearch}
        >
            <div className="photo" style={{ backgroundImage: `url(${performerPhotoUrl})` }} />
            <div className="info">
                <Highlighter
                    key={`related-publisher-tile-${author.id}`}
                    autoEscape={true}
                    className="primary"
                    highlightClassName="related-highlight"
                    searchWords={[keyWord!]}
                    textToHighlight={performerFullname!}
                />
                {/* TODO [i18n] css adjust font size*/}
                <span className="description">
                    <Trans
                        i18nKey="components.searchResults.relatedText"
                        components={{ span: <span className="related-keyword" /> }}
                        values={{ keyword: keyWord }}
                    />
                </span>
            </div>
            <i className="material-icons dym-ic">arrow_forward</i>
        </Link>
    );
};

type SearchRelatedNarratorProps = {
    readonly narrator: PerformerSidebarOut;
    readonly keyWord?: string;
    readonly onSearchRelatedClickResetSearch: () => void;
    readonly shopLanguage: string | undefined;
};

export const SearchRelatedNarrator: FC<SearchRelatedNarratorProps> = ({
    keyWord,
    narrator,
    onSearchRelatedClickResetSearch,
    shopLanguage
}) => {
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const env = useEnvironment();

    const performerPhotoUrl = MetadataUtils.performerPhotoUrl(narrator.id, env);
    const performerFullname = MetadataUtils.getPerformerFullname(narrator);

    return (
        <Link
            key={narrator.id}
            className="person related-element"
            to={Utils.topHitLocationDescriptor({
                filter: { narrator: { include: [narrator.id] } },
                scope: null,
                topHitLabel: performerFullname,
                isMultiLanguageShopEnabled,
                shopLanguage,
                ageCategory: undefined
            })}
            onClick={onSearchRelatedClickResetSearch}
        >
            <div className="photo" style={{ backgroundImage: `url(${performerPhotoUrl})` }} />
            <div className="info">
                <Highlighter
                    key={`related-publisher-tile-${narrator.id}`}
                    autoEscape={true}
                    className="primary"
                    highlightClassName="related-highlight"
                    searchWords={[keyWord!]}
                    textToHighlight={performerFullname!}
                />
                {/* TODO [i18n] css adjust font size*/}
                <span className="description">
                    <Trans
                        i18nKey="components.searchResults.relatedText"
                        components={{ span: <span className="related-keyword" /> }}
                        values={{ keyword: keyWord }}
                    />
                </span>
            </div>
            <i className="material-icons dym-ic">arrow_forward</i>
        </Link>
    );
};

type SearchRelatedGenreProps = {
    readonly genre: GenreSidebarOut;
    readonly keyWord?: string;
    readonly onSearchRelatedClickResetSearch: () => void;
    readonly shopLanguage: string | undefined;
};

export const SearchRelatedGenre: FC<SearchRelatedGenreProps> = ({
    genre: { id, name, sidebarTopHit },
    keyWord,
    onSearchRelatedClickResetSearch,
    shopLanguage
}) => {
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);

    return (
        <div className="related-genre related-element">
            <Link
                to={Utils.topHitLocationDescriptor({
                    filter: { genre: { include: [id] } },
                    scope: null,
                    topHitLabel: name,
                    isMultiLanguageShopEnabled,
                    shopLanguage: shopLanguage,
                    ageCategory: undefined
                })}
                onClick={onSearchRelatedClickResetSearch}
            >
                {sidebarTopHit ? (
                    <div className="txt-normal">
                        <Highlighter
                            key={`related-publisher-tile-${name}`}
                            autoEscape={true}
                            highlightClassName="related-highlight"
                            searchWords={[keyWord!]}
                            textToHighlight={name}
                        />
                    </div>
                ) : (
                    <div>{name}</div>
                )}
                {sidebarTopHit ? null : (
                    <div className="related-genre-description">
                        <Trans
                            i18nKey="components.searchResults.containsText"
                            components={{ span: <span className="related-keyword" /> }}
                            values={{ keyword: keyWord }}
                        />
                    </div>
                )}
                <i className="material-icons dym-ic">arrow_forward</i>
            </Link>
        </div>
    );
};

type SearchRelatedPublisherProps = {
    readonly publisher: PublisherSidebarOut;
    readonly keyWord?: string;
    readonly onSearchRelatedClickResetSearch: () => void;
    readonly shopLanguage: string | undefined;
};

export const SearchRelatedPublisher: FC<SearchRelatedPublisherProps> = ({
    keyWord,
    onSearchRelatedClickResetSearch,
    publisher: { name, sidebarTopHit },
    shopLanguage
}) => {
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);

    return (
        <div className="related-genre related-element">
            <Link
                to={Utils.topHitLocationDescriptor({
                    filter: { publisher: { include: [name] } },
                    scope: null,
                    shopLanguage: shopLanguage,
                    isMultiLanguageShopEnabled,
                    ageCategory: undefined
                })}
                onClick={onSearchRelatedClickResetSearch}
            >
                {sidebarTopHit ? (
                    <div className="txt-normal">
                        <Highlighter
                            key={`related-publisher-tile-${name}`}
                            autoEscape={true}
                            highlightClassName="related-highlight"
                            searchWords={[keyWord!]}
                            textToHighlight={name}
                        />
                    </div>
                ) : (
                    <div>{name}</div>
                )}
                {sidebarTopHit ? null : (
                    <div className="related-genre-description">
                        <Trans
                            i18nKey="components.searchResults.containsText"
                            components={{ span: <span className="related-keyword" /> }}
                            values={{ keyword: keyWord }}
                        />
                    </div>
                )}
                <i className="material-icons dym-ic">arrow_forward</i>
            </Link>
        </div>
    );
};
